import React, { useState, useEffect } from "react";
import axios from "axios";

import {
  Box,
  Paper,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  CircularProgress,
  Pagination,
  Stack,
} from "@mui/material";

import DownloadIcon from "@mui/icons-material/Download";

import Navbar from "../Navbar";
import Sidebar from "../Sidebar";

export default function CallReport() {
  const { API_CONFIG } = require("../../configuration");

  const [loading, setLoading] = useState(false);
  const [sheets, setSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState("");
  const [rows, setRows] = useState([]);

  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  // Load Sheets
  const fetchSheets = async () => {
    try {
      const token = localStorage.getItem("token");

      const response = await axios.get(
        `${API_CONFIG.APIURL}/call/track/sheetsList`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setSheets(response.data || []);
    } catch (error) {
      console.error("Sheet Fetch Error:", error);
    }
  };

  // Load Report
  const fetchReport = async (sheetId) => {
    try {
      setLoading(true);

      const token = localStorage.getItem("token");
      const user = JSON.parse(localStorage.getItem("user"));
      const branch = user?.branch;

      const response = await axios.get(
        `${API_CONFIG.APIURL}/call/report/${sheetId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            branch,
          },
        },
      );

      setRows(response?.data?.data || []);
      setPage(1);
    } catch (error) {
      console.error("Report Fetch Error:", error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSheets();
  }, []);

  const handleSheetChange = (event) => {
    const sheetId = event.target.value;

    setSelectedSheet(sheetId);

    if (sheetId) {
      fetchReport(sheetId);
    }
  };

  const handleDownload = async () => {
    try {
      const token = localStorage.getItem("token");
      const user = JSON.parse(localStorage.getItem("user"));

      const response = await axios.get(
        `${API_CONFIG.APIURL}/call/report/${selectedSheet}`,
        {
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            branch: user?.branch,
            export: true,
          },
        },
      );

      const selectedSheetObj = sheets.find((s) => s.id === selectedSheet);

      const fileName = selectedSheetObj?.sheet_name || "Report";

      const url = window.URL.createObjectURL(new Blob([response.data]));

      const link = document.createElement("a");
      link.href = url;
      link.download = `${fileName}.xlsx`;

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
    }
  };

  const columns =
    rows.length > 0
      ? Object.keys(rows[0]).filter(
          (key) => key !== "id" && key !== "customer_data_id",
        )
      : [];

  const totalPages = Math.ceil(rows.length / rowsPerPage);

  const paginatedRows = rows.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage,
  );

  return (
    <>
      <Navbar />
      <Sidebar />

      <Box
        sx={{
          p: { xs: 1.5, sm: 2, md: 3 },
          ml: { xs: 0, md: "250px" },
          width: {
            xs: "100%",
            md: "calc(100% - 250px)",
          },
          boxSizing: "border-box",
          overflowX: "hidden",
          backgroundColor: "#F9FAFB",
          minHeight: "100vh",
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 3,
            borderRadius: 3,
          }}
        >
          {/* Header */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
            mb={3}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
              }}
            >
              Report
            </Typography>

            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              disabled={!selectedSheet}
            >
              Download Report
            </Button>
          </Box>

          {/* Filters */}
          <Box display="flex" gap={2} mb={3} flexWrap="wrap">
            <FormControl sx={{ minWidth: 350 }}>
              <InputLabel>Select Sheet</InputLabel>

              <Select
                value={selectedSheet}
                label="Select Sheet"
                onChange={handleSheetChange}
              >
                {sheets.map((sheet) => (
                  <MenuItem key={sheet.id} value={sheet.id}>
                    {sheet.sheet_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Loader */}
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height={400}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer
                component={Paper}
                sx={{
                  maxHeight: "70vh",
                  border: "1px solid #ddd",
                }}
              >
                <Table
                  stickyHeader
                  size="small"
                  sx={{
                    minWidth: 2500,
                  }}
                >
                  <TableHead>
                    <TableRow>
                      {columns.map((column) => (
                        <TableCell
                          key={column}
                          sx={{
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                            backgroundColor: "#1976d2",
                            color: "#fff",
                          }}
                        >
                          {column.replaceAll("_", " ").toUpperCase()}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {paginatedRows.length > 0 ? (
                      paginatedRows.map((row, index) => (
                        <TableRow key={index} hover>
                          {columns.map((column) => (
                            <TableCell
                              key={column}
                              sx={{
                                whiteSpace:
                                  column === "remarks1" ? "pre-line" : "nowrap",
                                minWidth: column === "remarks1" ? 500 : "auto",
                                verticalAlign: "top",
                              }}
                            >
                              {row[column] ?? "-"}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell align="center" colSpan={columns.length}>
                          No Records Found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {rows.length > rowsPerPage && (
                <Stack spacing={2} mt={3} alignItems="center">
                  <Pagination
                    page={page}
                    count={totalPages}
                    color="primary"
                    onChange={(e, value) => setPage(value)}
                  />
                </Stack>
              )}
            </>
          )}
        </Paper>
      </Box>
    </>
  );
}
